import type { Replace, Translate } from 'common/providers/LocaleProvider';
import type { FormFieldValidationFunction, ValidationRule } from '../../DynamicForm/types';
import type { BirthDayFieldValue } from './types';

interface TranslateArgs {
  t: Translate;
  r: Replace;
}

const getMaxDay = ({ month, year }: BirthDayFieldValue) => {
  if (!month) return 31;

  if (['1', '3', '5', '7', '8', '10', '12'].includes(month)) {
    return 31;
  } else if (['4', '6', '9', '11'].includes(month)) {
    return 30;
  } else {
    if (!year) return 28;
    const isLeap = new Date(parseInt(year, 10), 1, 29).getMonth() === 1;

    return isLeap ? 29 : 28;
  }
};

const validateDay = (r: Replace) => (value: BirthDayFieldValue) => {
  const day = parseInt(value.day, 10);
  const max = getMaxDay(value);

  if (isNaN(day) || day < 1 || day > max) return r.t('h24_birthday_day_error', { max });

  return true;
};

const validateYear = (t: Translate) => (value: BirthDayFieldValue) => {
  const YEAR_INPUT_LENGTH = 4;
  const year = parseInt(value.year, 10);

  if (isNaN(year) || value.year.length !== YEAR_INPUT_LENGTH) return t('h24_birthday_year_error');

  return true;
};

const validateMonth = (t: Translate) => (value: BirthDayFieldValue) => {
  const month = parseInt(value.month, 10);

  if (isNaN(month) || month < 1 || month > 12) return t('h24_birthday_month_error');

  return true;
};

const areFieldsValid = (t: Translate) => (value: BirthDayFieldValue) => {
  if (!value.day || !value.month || !value.year) return false;

  if (
    typeof validateMonth(t)(value) === 'string' ||
    typeof validateYear(t)(value) === 'string' ||
    typeof validateDay({ t } as Replace)(value) === 'string'
  )
    return false;

  return true;
};

const isDateValid = (value: BirthDayFieldValue) => {
  const month = parseInt(value.month, 10) - 1;
  const day = parseInt(value.day, 10);
  const year = parseInt(value.year, 10);
  const date = new Date(year, month, day);

  if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
    return date;
  }

  return false;
};

const validateDate = (t: Translate) => (value: BirthDayFieldValue) => {
  if (!areFieldsValid(t)(value)) return true;

  if (!isDateValid(value)) return t('h24_birthday_date_error');

  return true;
};

const getBirthdayFieldValidators = ({ t, r }: TranslateArgs) =>
  ({
    day: validateDay(r),
    month: validateMonth(t),
    year: validateYear(t),
    date: validateDate(t),
  }) as unknown as Record<string, FormFieldValidationFunction>;

export const getBirthdayFieldValidations = ({
  t,
  r,
  gqlValidationRules,
}: TranslateArgs & { gqlValidationRules: ValidationRule[] }) => ({
  required: false,
  rules: gqlValidationRules,
  functions: getBirthdayFieldValidators({ t, r }),
});

export const getValidDate = (t: Translate) => (value: BirthDayFieldValue) => {
  if (!areFieldsValid(t)(value)) return null;
  const date = isDateValid(value);

  return date ? date : null;
};
